import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Suppress the unconditional "🌐 i18next is made possible by ... Locize" promo
// that i18next prints to console.info on first init. The text contains the
// word "AI" and clutters production consoles. i18next gates this on a static
// flag; setting it before init skips the message in all envs.
(i18n as unknown as { welcomeMessageDisplayed: boolean }).welcomeMessageDisplayed = true;

import commonEN from "../../public/locales/en/common.json";
import accountEN from "../../public/locales/en/account.json";
import dashboardEN from "../../public/locales/en/dashboard.json";
import shiftsEN from "../../public/locales/en/shifts.json";
import invoicesEN from "../../public/locales/en/invoices.json";
import clientsEN from "../../public/locales/en/clients.json";
import landingEN from "../../public/locales/en/landing.json";
import familyEN from "../../public/locales/en/family.json";
import profileEN from "../../public/locales/en/profile.json";

export const LANGUAGES = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "es", name: "Spanish", nativeName: "Espa\u00f1ol" },
  { code: "fr", name: "French", nativeName: "Fran\u00e7ais" },
  { code: "ht", name: "Haitian Creole", nativeName: "Krey\u00f2l Ayisyen" },
  { code: "pt", name: "Portuguese", nativeName: "Portugu\u00eas" },
  { code: "zh", name: "Chinese", nativeName: "\u4e2d\u6587" },
  { code: "hi", name: "Hindi", nativeName: "\u0939\u093f\u0928\u094d\u0926\u0940" },
  { code: "ar", name: "Arabic", nativeName: "\u0627\u0644\u0639\u0631\u0628\u064a\u0629" },
  { code: "tl", name: "Tagalog", nativeName: "Tagalog" },
  { code: "vi", name: "Vietnamese", nativeName: "Ti\u1ebfng Vi\u1ec7t" },
  { code: "ko", name: "Korean", nativeName: "\ud55c\uad6d\uc5b4" },
  { code: "ja", name: "Japanese", nativeName: "\u65e5\u672c\u8a9e" },
  { code: "de", name: "German", nativeName: "Deutsch" },
  { code: "it", name: "Italian", nativeName: "Italiano" },
  { code: "ru", name: "Russian", nativeName: "\u0420\u0443\u0441\u0441\u043a\u0438\u0439" },
] as const;

export type Locale = (typeof LANGUAGES)[number]["code"];

// NOTE on key structure (accepted debt, eng-review 2026-06-09): locale files
// mix nested objects, top-level string keys ("schedule"), and flat dotted
// keys ("schedule.month"). Flat dotted keys resolve only via i18next's
// ignoreJSONStructure default — do NOT set keySeparator: false (it would
// break the nested keys) and avoid adding a nested group whose name collides
// with an existing top-level string key. test/i18n-parity.test.ts guards the
// key sets either way.
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "en",
    defaultNS: "common",
    fallbackNS: "common",
    ns: ["common", "account", "dashboard", "shifts", "invoices", "clients", "landing", "family", "profile"],
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "carelogic-locale",
      caches: ["localStorage"],
    },
    interpolation: { escapeValue: false },
    resources: {
      en: {
        common: commonEN,
        account: accountEN,
        dashboard: dashboardEN,
        shifts: shiftsEN,
        invoices: invoicesEN,
        clients: clientsEN,
        landing: landingEN,
        family: familyEN,
        profile: profileEN,
      },
    },
  });

/** Load a language's translations dynamically */
export async function loadLanguage(lang: string): Promise<void> {
  if (i18n.hasResourceBundle(lang, "common")) return;
  try {
    const namespaces = ["common", "account", "dashboard", "shifts", "invoices", "clients", "landing", "family", "profile"];
    const results = await Promise.all(
      namespaces.map((ns) => fetch(`/locales/${lang}/${ns}.json`).then((r) => (r.ok ? r.json() : null)).catch(() => null)),
    );
    namespaces.forEach((ns, i) => {
      if (results[i]) i18n.addResourceBundle(lang, ns, results[i]);
    });
  } catch {
    // Fall back to English silently
  }
}

export default i18n;
